import {
  AbsoluteCenter,
  Box,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import {
  AnimatedLogo,
  CustomDrawer,
  CustomDrawerBackButton,
  CustomDrawerFooter,
  CustomFormErrorMessage,
  PrimaryButton,
  StatusBox,
} from '../../../components/custom';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  resetBillBeneficiaryCreation,
  saveBillBeneficiaryAsync,
} from '../../../store/slices/billPaymentBeneficiaries/saveBeneficiarySlice';
import { TApiStatus } from '../../../types/api';

export interface IBillDetails {
  customerName: string;
  customerId: string;
  billCategory: string;
  billItemId: string;
  billerName: string;
  billerId: string;
}

interface IBeneficiarySummaryProps {
  isBeneficiaryDrawerOpen: boolean;
  setIsBeneficiaryDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  billClose?: () => void;
  billDetails?: IBillDetails;
}

export default function BeneficiarySummary({
  isBeneficiaryDrawerOpen,
  setIsBeneficiaryDrawerOpen,
  billClose,
  billDetails,
}: IBeneficiarySummaryProps) {
  const dispatch = useAppDispatch();
  const { status, value } = useAppSelector(
    (state) => state.saveBillBeneficiary
  );

  const errorMessage = useGetUIErrorMessage({
    message: value?.message,
    errors: value?.errors,
  });

  const initialValues = {
    alias: '',
  };

  const validationSchema = Yup.object().shape({
    alias: Yup.string(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      // Do nothing
    },
  });

  const { errors, touched, values, getFieldProps } = formik;

  const savePayload = {
    customerId: billDetails?.customerId ?? '',
    billerName: billDetails?.billerName ?? '',
    billCategory: billDetails?.billCategory ?? '',
    billerId: billDetails?.billerId ?? '',
    billItemId: billDetails?.billItemId ?? '',
    alias: values.alias ?? '',
  };

  const onSaveBeneficiary = () => {
    dispatch(saveBillBeneficiaryAsync(savePayload));
  };

  return (
    <CustomDrawer
      isOpen={isBeneficiaryDrawerOpen}
      onClose={() => {
        setIsBeneficiaryDrawerOpen(false);
      }}
      drawerHeader="Save Beneficiary"
    >
      {status === ('loading' as TApiStatus) ? (
        <Box minH="330px">
          <AbsoluteCenter>
            <AnimatedLogo />
          </AbsoluteCenter>
        </Box>
      ) : status === ('success' as TApiStatus) ? (
        <>
          <Box minH="330px" mb="82px">
            <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
              <StatusBox
                variant="success"
                title="Beneficiary Information Saved Successfully."
              >
                {errorMessage}
              </StatusBox>
            </AbsoluteCenter>
          </Box>
          <CustomDrawerFooter>
            <PrimaryButton
              w="full"
              top="initial"
              right="initial"
              onClick={() => {
                dispatch(resetBillBeneficiaryCreation());
                setIsBeneficiaryDrawerOpen(false);
                billClose?.();
              }}
            >
              Dismiss
            </PrimaryButton>
          </CustomDrawerFooter>
        </>
      ) : status === ('failed' as TApiStatus) ? (
        <>
          <Box minH="330px" mb="82px">
            <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
              <StatusBox variant="error" title="Failed to save beneficiary.">
                {errorMessage}
              </StatusBox>
            </AbsoluteCenter>
          </Box>
          <CustomDrawerFooter>
            <PrimaryButton
              onClick={() => dispatch(resetBillBeneficiaryCreation())}
              w="full"
            >
              Retry
            </PrimaryButton>
          </CustomDrawerFooter>
        </>
      ) : (
        <>
          <CustomDrawerBackButton
            onClick={() => setIsBeneficiaryDrawerOpen(false)}
          />
          <Box w="full" marginTop={4}>
            <Box mb={6}>
              <Text color="#000" mb={1}>
                Beneficiary Information
              </Text>
              <Text color="gray.300" fontSize=".875rem" lineHeight="171.5%">
                Please enter beneficiary details below.
              </Text>
            </Box>
            <Stack
              gap={6}
              sx={{
                border: '1px solid black',
                padding: '16px',
                borderRadius: '16px',
              }}
            >
              <Stack
                gap={4}
                sx={{
                  padding: '16px',
                  backgroundColor: '#FAFAFA',
                  borderRadius: '12px',
                  border: '1px solid #F5F6F6',
                }}
              >
                <Text
                  sx={{
                    fontSize: '.875rem',
                    fontWeight: '500',
                  }}
                >
                  Recipient Details
                </Text>
                <Stack gap={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text
                      sx={{
                        color: 'richBlack.300',
                        fontSize: '.875rem',
                      }}
                    >
                      Provider
                    </Text>
                    <Text
                      sx={{
                        color: 'richBlack.300',
                        fontSize: '.875rem',
                        fontWeight: '500',
                      }}
                    >
                      {/* <Icon>
                      {getNetworkIcon(
                        billDetails?.billerName ?? 'Airtel Airtime'
                      )}
                    </Icon> */}
                      {billDetails?.billerName}
                    </Text>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text
                      sx={{
                        color: 'richBlack.300',
                        fontSize: '.875rem',
                      }}
                    >
                      Account Name
                    </Text>
                    <Text
                      sx={{
                        color: 'richBlack.300',
                        fontSize: '.875rem',
                        fontWeight: '500',
                      }}
                    >
                      {billDetails?.customerName}
                    </Text>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text
                      sx={{
                        color: 'richBlack.300',
                        fontSize: '.875rem',
                      }}
                    >
                      Customer ID
                    </Text>
                    <Text
                      sx={{
                        color: 'richBlack.300',
                        fontSize: '.875rem',
                        fontWeight: '500',
                      }}
                    >
                      {billDetails?.customerId}
                    </Text>
                  </Box>
                </Stack>
              </Stack>
              <Divider />
              <Box>
                <FormikProvider value={formik}>
                  <Form autoComplete="off" noValidate>
                    <FormControl
                      id="alias"
                      isInvalid={Boolean(touched.alias && errors.alias)}
                    >
                      <FormLabel
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text>Beneficiary Alias </Text>{' '}
                        <Text color="richBlack.200">Optional</Text>
                      </FormLabel>
                      <Input
                        {...getFieldProps('alias')}
                        as="input"
                        placeholder="Enter Beneficiary Alias"
                        isInvalid={Boolean(touched.alias && errors.alias)}
                      />
                      <CustomFormErrorMessage>
                        {touched.alias && errors.alias}
                      </CustomFormErrorMessage>
                    </FormControl>
                  </Form>
                </FormikProvider>
              </Box>
            </Stack>
            <CustomDrawerFooter>
              <PrimaryButton onClick={onSaveBeneficiary} type="submit" w="full">
                Save Beneficiary
              </PrimaryButton>
            </CustomDrawerFooter>
          </Box>
        </>
      )}
    </CustomDrawer>
  );
}
